import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaExternalLinkAlt, FaPen } from 'react-icons/fa';
import Btn from '../../components/ui/Btn';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { statusTag } from './AgentsList';
import ProjectLink from './components/ProjectLink';
import WhereItLives, { validUrl } from './components/WhereItLives';
import RenameAgentModal from './components/RenameAgentModal';
import { MANAGED_LABELS, EXTERNAL_MEANING } from '../../utils/agentReadiness';

// Agente externo: vive en otro sistema. Aquí solo se ve dónde está; se puede editar ese dato, pasarlo a la plataforma o archivarlo.
export default function ExternalAgentView({ data, actions }) {
  const { agent, project } = data;
  const [where, setWhere] = useState(agent.managed_by);
  const [url, setUrl] = useState(agent.external_url || '');
  const [note, setNote] = useState(agent.external_note || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [renaming, setRenaming] = useState(false);
  useEffect(() => { setWhere(agent.managed_by); setUrl(agent.external_url || ''); setNote(agent.external_note || ''); }, [agent]);

  const urlError = !validUrl(url) ? 'Debe empezar con http:// o https://.' : '';
  const dirty = where !== agent.managed_by || url !== (agent.external_url || '') || note !== (agent.external_note || '');
  const archived = agent.status === 'archived';

  const save = async () => {
    if (urlError) return;
    setBusy(true); setError('');
    try { await actions.setManagedBy({ managedBy: where, externalUrl: url, externalNote: note }); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <Link to="/agentes" className="inline-flex items-center gap-2 text-[13px] text-fg-dim hover:text-fg rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">
        <FaArrowLeft aria-hidden className="h-3 w-3" /> Agentes
      </Link>
      <header className="mt-3 flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-[28px] leading-tight font-semibold tracking-[-0.02em] text-fg break-words">{agent.name}</h1>
            {!archived && actions.rename && (
              <button type="button" onClick={() => setRenaming(true)} aria-label="Cambiar el nombre del agente" title="Cambiar el nombre"
                className="grid place-items-center h-8 w-8 rounded-full text-fg-dim hover:text-fg hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">
                <FaPen aria-hidden className="h-3 w-3" />
              </button>
            )}
            {statusTag(agent)}
          </div>
          <p className="mt-1 text-sm text-fg-dim"><ProjectLink project={project} agent={agent} onOpen={actions.openProject} /></p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {archived ? <Btn onClick={() => setConfirm('restore')}>Restaurar</Btn> : <Btn variant="ghost" onClick={() => setConfirm('archive')}>Archivar</Btn>}
          <Btn onClick={() => setConfirm('platform')}>Configurarlo aquí</Btn>
        </div>
      </header>

      <div className="mt-6 rounded-xl border border-line bg-panel/60 p-5">
        <p className="text-sm text-fg">Este agente es externo: funciona en {MANAGED_LABELS[agent.managed_by]}.</p>
        <p className="mt-1 text-[13px] leading-relaxed text-fg-dim">{EXTERNAL_MEANING}</p>
        {agent.external_url && (
          <a href={agent.external_url} target="_blank" rel="noreferrer noopener"
            className="mt-3 inline-flex items-center gap-2 text-[13px] text-live hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">
            Abrir donde vive <FaExternalLinkAlt aria-hidden className="h-3 w-3" /><span className="sr-only">(se abre en otra pestaña)</span>
          </a>
        )}
      </div>

      <section className="mt-8 space-y-4" aria-labelledby="where-title">
        <h2 id="where-title" className="text-[15px] font-semibold text-fg">Dónde vive</h2>
        <WhereItLives value={where} onChange={setWhere} url={url} onUrl={setUrl} note={note} onNote={setNote} urlError={urlError} hidePlatform />
        <div className="flex items-center gap-3">
          <Btn variant={dirty ? 'primary' : 'quiet'} onClick={save} loading={busy} disabled={!dirty || Boolean(urlError)}>Guardar</Btn>
          {error && <span role="alert" className="text-[12.5px] text-signal">{error}</span>}
        </div>
      </section>

      <ConfirmModal open={confirm === 'platform'} onClose={() => setConfirm(null)} title="Configurarlo aquí" confirmLabel="Configurarlo aquí"
        onConfirm={() => actions.setManagedBy({ managedBy: 'platform' })}>
        <p>Deja de ser externo y aparece la ruta de puesta en marcha: clave, instrucciones, publicación, canal y motor.</p>
        <p>No cambia nada donde vive hoy: {MANAGED_LABELS[agent.managed_by]} sigue atendiendo hasta que conectes un canal aquí y enciendas el motor del proyecto.</p>
      </ConfirmModal>
      <ConfirmModal open={confirm === 'archive'} onClose={() => setConfirm(null)} title="Archivar el agente" confirmLabel="Archivar" tone="danger" onConfirm={actions.archive}>
        <p>Sale de la lista de agentes en uso. No cambia nada donde vive; puedes restaurarlo.</p>
      </ConfirmModal>
      <RenameAgentModal open={renaming} onClose={() => setRenaming(false)} agent={agent} onSave={actions.rename} />
      <ConfirmModal open={confirm === 'restore'} onClose={() => setConfirm(null)} title="Restaurar el agente" confirmLabel="Restaurar" onConfirm={actions.restore}>
        <p>Vuelve a la lista de agentes en uso.</p>
      </ConfirmModal>
    </div>
  );
}
